import { formatPointsLabel, getBetPoints, type LegacyScoringSystem, type ScoringBetInput } from "./scoring";
import { buildSpreadScoreAxisMarks, buildSpreadScoreBars } from "./spreadScoreBars";

export type LiveActivityTone = "good" | "bad" | "neutral";

export type LiveActivityRow = {
  id: string;
  awayAbbr: string;
  awayScore: string;
  homeAbbr: string;
  homeScore: string;
  status: string;
  tone: LiveActivityTone;
  clock: string;
  pickLabel: string;
  chip: string;
};

export type LiveActivitySnapshot = {
  windowLabel: string;
  aheadLabel: string;
  focusKind: "game" | "potw";
  clockLabel: string;
  awayAbbr: string;
  homeAbbr: string;
  awayScore: string;
  homeScore: string;
  compactScore: string;
  pickLabel: string;
  compactStatus: string;
  expandedStatus: string;
  statusTone: LiveActivityTone;
  rows: LiveActivityRow[];
  potwActive: boolean;
  potwCompactName: string;
  potwCurrent: string;
  potwLine: string;
  potwTitle: string;
  potwRemaining: string;
  potwProgress: number;
  /** "SEA ML", "SEA +3.5", or "OVER 47.5". */
  pickHeadline: string;
  /** Empty for a team pick. The away abbreviation when the pick is a total. */
  scorePrefix: string;
  pickedScore: string;
  opponentScore: string;
  opponentAbbr: string;
  /** Which side of the scoreboard the user took. Empty for a total. */
  pickedSide: "" | "away" | "home";
  /** Spread shown under that team. Empty when this side was not the pick. */
  awayMark: string;
  homeMark: string;
  /** Pill copy, e.g. "TRAILING by 4" or "PICK WON". */
  statusSentence: string;
  /** "Worth 25 pts" while live, "+25 pts" after a win. */
  stakeLabel: string;
  aheadCount: number;
  pickCount: number;
  /** "of 4 ahead" when more than one pick is on the card. Empty for a single pick. */
  summaryTail: string;
  /** "3 OF 4 PICKS AHEAD" when more than one pick is on the card. */
  summaryTitle: string;
  /** Right side of the masthead: a clock, "4 GAMES LIVE", or "FINAL". */
  headerDetail: string;
  /** Prop pill, e.g. "AHEAD +45.5" or "Needs 23.5". */
  potwChip: string;
  /** "line" for over/under, "matchup" for a two-player duel. */
  potwKind: "" | "line" | "matchup";
  potwPlayerOne: string;
  potwPlayerTwo: string;
  potwValueOne: string;
  potwValueTwo: string;
  potwAbbrOne: string;
  potwAbbrTwo: string;
  potwPicked: "" | "one" | "two";
  potwPickLabel: string;
  /** True when the focused pick is a spread and both scores are in. */
  spreadGraph: boolean;
  spreadHeadline: string;
  spreadSubhead: string;
  /** Bar lengths as fractions of the longer bar, from 0 to 1. */
  awayFill: number;
  homeFill: number;
  awayBoost: number;
  homeBoost: number;
  awayBoostLabel: string;
  homeBoostLabel: string;
  spreadResult: "" | "covering" | "trailing" | "push";
  spreadAxis: string;
  /**
   * file URL prefix for local team marks in the app-group container, including
   * the trailing slash. Empty when the marks are not on disk yet.
   */
  logoBase: string;
};

export type LiveActivityWeekGame = {
  id: string;
  gameTime: string;
  isCompleted: boolean;
  homeScore: number | null;
  awayScore: number | null;
  homeAbbreviation: string;
  awayAbbreviation: string;
  homeLineScores?: unknown;
  period?: string | null;
  clock?: string | null;
  status?: string | null;
  /** Provider string such as "6:42 - 2nd" or "HALFTIME". */
  liveStatus?: string | null;
};

export type LiveActivityPick = {
  gameId: string;
  betType: "moneyline" | "spread" | "total" | "custom_prop" | "total_tiebreaker";
  side: string;
  spread: number | null;
  total: number | null;
  /** Precomputed PCKM points for this pick. Omitted when the stake is unknown. */
  points?: number | null;
};

export type LiveActivityPotw = {
  gameId: string;
  playerName: string;
  statLabel: string;
  current: number;
  line: number;
  /** Side one is over. Side two is under. For a matchup, side one is player one. */
  side: "one" | "two" | null;
  kind?: "line" | "matchup";
  playerTwoName?: string;
  playerTwoValue?: number;
  playerOneAbbr?: string;
  playerTwoAbbr?: string;
};

const BET_PRIORITY: Record<LiveActivityPick["betType"], number> = {
  spread: 0,
  moneyline: 1,
  total: 2,
  custom_prop: 3,
  total_tiebreaker: 4,
};

const formatNumber = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const formatSigned = (value: number): string => {
  if (value > 0) return `+${formatNumber(value)}`;
  if (value < 0) return `-${formatNumber(Math.abs(value))}`;
  return "0";
};

const needLabel = (margin: number): string => {
  const need = Math.max(1, Math.ceil(Math.abs(margin) - 1e-9));
  return `NEED +${need}`;
};

const quarterFromLineScores = (value: unknown): string | null => {
  if (Array.isArray(value)) {
    const filled = value.filter((entry) => typeof entry === "number" && Number.isFinite(entry)).length;
    if (filled >= 5) return "OT";
    if (filled >= 1) return `Q${Math.min(filled, 4)}`;
    return null;
  }
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const keys = ["quarter1", "quarter2", "quarter3", "quarter4", "overtime"] as const;
  let last: string | null = null;
  keys.forEach((key, index) => {
    const score = record[key];
    if (typeof score === "number" && Number.isFinite(score)) {
      last = index === 4 ? "OT" : `Q${index + 1}`;
    }
  });
  return last;
};

const PERIOD_LABEL: Record<string, string> = {
  "1ST": "Q1",
  "2ND": "Q2",
  "3RD": "Q3",
  "4TH": "Q4",
  OT: "OT",
  OVERTIME: "OT",
};

/** Turn a provider status into a lock-screen clock such as "Q2 · 6:42" or "HALFTIME". */
export function formatNflLiveClock(status: string | null | undefined): string | null {
  const raw = status?.trim();
  if (!raw) return null;
  const upper = raw.toUpperCase().replace(/\./g, "");
  if (/\b(FINAL|FT)\b/.test(upper)) return "FINAL";
  if (/\b(HALFTIME|HALF TIME|\bHT\b|\bHALF\b)/.test(upper)) return "HALFTIME";
  const clocked = upper.match(/(\d{1,2}:\d{2})\s*[-–]\s*(1ST|2ND|3RD|4TH|OT|OVERTIME)/);
  const clock = clocked?.[1];
  const periodToken = clocked?.[2];
  if (clock && periodToken) {
    const period = PERIOD_LABEL[periodToken] ?? "LIVE";
    return `${period} · ${clock}`;
  }
  if (/^Q[1-4]$/.test(upper) || upper === "OT") return upper;
  return null;
}

export const liveActivityClockLabel = (game: LiveActivityWeekGame): string => {
  if (game.isCompleted) return "FINAL";
  const providerClock = formatNflLiveClock(game.liveStatus);
  if (providerClock && providerClock !== "FINAL") return providerClock;
  const period = game.period?.trim();
  const clock = game.clock?.trim();
  if (period || clock) return [period, clock].filter(Boolean).join(" ");
  const status = game.status?.trim();
  if (status && !["IN_PROGRESS", "LIVE", "SCHEDULED"].includes(status.toUpperCase())) {
    return status.toUpperCase();
  }
  return quarterFromLineScores(game.homeLineScores) ?? "LIVE";
};

const windowParts = (gameTime: string): { key: string; label: string } | null => {
  const date = new Date(gameTime);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    hourCycle: "h23",
    weekday: "short",
    dayPeriod: "short",
  }).formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  const hour12 = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    hourCycle: "h12",
  }).formatToParts(date);
  const hourLabel = hour12.find((part) => part.type === "hour")?.value ?? "";
  const period = hour12.find((part) => part.type === "dayPeriod")?.value ?? "";
  return {
    key: `${read("year")}-${read("month")}-${read("day")}-${read("hour")}`,
    label: `${read("weekday")} ${hourLabel}${period}`.toUpperCase(),
  };
};

type ScoredPick = {
  game: LiveActivityWeekGame;
  pick: LiveActivityPick;
  started: boolean;
  live: boolean;
  windowKey: string;
  windowLabel: string;
  tone: LiveActivityTone;
  status: string;
  expandedStatus: string;
  pickLabel: string;
  margin: number | null;
  pickedAbbr: string;
};

const scorePick = (game: LiveActivityWeekGame, pick: LiveActivityPick, now: Date): ScoredPick | null => {
  const window = windowParts(game.gameTime);
  if (!window) return null;
  const kickoff = new Date(game.gameTime).getTime();
  const started = Number.isFinite(kickoff) && kickoff <= now.getTime();
  const live = started && !game.isCompleted;
  const home = game.homeAbbreviation || "HOME";
  const away = game.awayAbbreviation || "AWAY";
  const homeScore = game.homeScore;
  const awayScore = game.awayScore;
  const hasScore = typeof homeScore === "number" && typeof awayScore === "number";
  const pickedHome = pick.side === "one";
  const pickedAbbr = pickedHome ? home : away;

  let tone: LiveActivityTone = "neutral";
  let status = live ? "LIVE" : game.isCompleted ? "FINAL" : "SOON";
  let expandedStatus = status;
  let pickLabel = pickedAbbr;
  let margin: number | null = null;

  if (pick.betType === "spread" && pick.spread !== null && hasScore) {
    const homeMargin = homeScore + pick.spread - awayScore;
    margin = pickedHome ? homeMargin : -homeMargin;
    pickLabel = `${pickedAbbr} ${formatSigned(pickedHome ? pick.spread : -pick.spread)}`;
    if (margin > 0) {
      tone = "good";
      status = `COVER ${formatSigned(margin)}`;
      expandedStatus = `COVERING ${formatSigned(margin)}`;
    } else if (margin === 0) {
      status = "PUSH";
      expandedStatus = "PUSH";
    } else {
      tone = "bad";
      status = needLabel(margin);
      expandedStatus = status;
    }
  } else if (pick.betType === "total" && pick.total !== null && hasScore) {
    const combined = homeScore + awayScore;
    margin = pick.side === "one" ? combined - pick.total : pick.total - combined;
    pickLabel = `${pick.side === "one" ? "OVER" : "UNDER"} ${formatNumber(pick.total)}`;
    if (margin > 0) {
      tone = "good";
      status = `COVER ${formatSigned(margin)}`;
      expandedStatus = `COVERING ${formatSigned(margin)}`;
    } else if (margin === 0) {
      status = "PUSH";
      expandedStatus = "PUSH";
    } else {
      tone = "bad";
      status = needLabel(margin);
      expandedStatus = status;
    }
  } else if (pick.betType === "moneyline" && hasScore) {
    if (homeScore === awayScore) {
      status = "TIED";
      expandedStatus = "TIED";
    } else {
      const homeLeading = homeScore > awayScore;
      const leading = pickedHome ? homeLeading : !homeLeading;
      tone = leading ? "good" : "bad";
      status = leading ? "LEADING" : "TRAILING";
      expandedStatus = status;
    }
  }

  return {
    game,
    pick,
    started,
    live,
    windowKey: window.key,
    windowLabel: window.label,
    tone,
    status,
    expandedStatus,
    pickLabel,
    margin,
    pickedAbbr,
  };
};

const compactPlayerName = (playerName: string): string => {
  const parts = playerName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "POTW";
  if (parts.length >= 3) {
    return parts.map((part) => part[0] ?? "").join("").toUpperCase().slice(0, 4);
  }
  const last = parts[parts.length - 1] ?? "POTW";
  return last.slice(0, 6).toUpperCase();
};

const lastName = (playerName: string): string => {
  const parts = playerName.trim().split(/\s+/).filter(Boolean);
  return parts[parts.length - 1] ?? playerName;
};

const RECENT_FINAL_MS = 6 * 60 * 60 * 1000;

/** Lock Screen Live Activities clip above 160pt. Three compact rows fit; four do not. */
export const LIVE_ACTIVITY_LOCK_SCREEN_ROWS = 3;

export function liveActivityPickPoints(input: {
  scoringSystem?: LegacyScoringSystem | null;
  betType: LiveActivityPick["betType"];
  side: "one" | "two";
  oddsOne?: number | null;
  oddsTwo?: number | null;
  displayInfo?: Record<string, unknown> | null;
  scoringConfigOverride?: ScoringBetInput["scoringConfigOverride"];
}): number {
  return getBetPoints({
    scoringSystem: input.scoringSystem,
    bet: {
      betType: input.betType,
      oddsOne: input.oddsOne,
      oddsTwo: input.oddsTwo,
      displayInfo: input.displayInfo,
      scoringConfigOverride: input.scoringConfigOverride,
    },
    side: input.side,
  });
}

const stakeLabelFor = (
  points: number | null | undefined,
  outcome: "live" | "won" | "lost" | "push",
): string => {
  if (outcome === "push") return "";
  if (typeof points !== "number" || !Number.isFinite(points) || points <= 0) return "";
  const label = formatPointsLabel(points);
  if (outcome === "won") return `+${label}`;
  if (outcome === "lost") return "0 pts";
  return `Worth ${label}`;
};

const scoreDiff = (game: LiveActivityWeekGame): number | null => {
  if (typeof game.homeScore !== "number" || typeof game.awayScore !== "number") return null;
  return Math.abs(game.homeScore - game.awayScore);
};

const pickKindLabel = (entry: ScoredPick): string => (
  entry.pick.betType === "moneyline" ? `${entry.pickedAbbr} ML` : entry.pickLabel
);

const chipLabel = (entry: ScoredPick): string => {
  if (!entry.started) return "Pending";
  if (entry.pick.betType === "moneyline") {
    if (entry.game.isCompleted) {
      if (entry.tone === "good") return "Won";
      if (entry.tone === "bad") return "Lost";
      return "Tied";
    }
    if (entry.status === "TIED") return "Tied";
    const diff = scoreDiff(entry.game);
    if (diff === null || diff === 0) return "Tied";
    return entry.tone === "good" ? `Up ${diff}` : `Trailing by ${diff}`;
  }
  if (entry.margin === null) return entry.game.isCompleted ? (entry.tone === "good" ? "Won" : "Lost") : "Pending";
  if (entry.margin === 0) return "Push";
  if (entry.game.isCompleted && entry.tone === "bad") return `Missed by ${formatNumber(Math.abs(entry.margin))}`;
  return entry.margin > 0
    ? `Up ${formatNumber(entry.margin)}`
    : `Trailing by ${formatNumber(Math.abs(entry.margin))}`;
};

const spreadGraphFor = (entry: ScoredPick) => {
  const empty = {
    spreadGraph: false,
    spreadHeadline: "",
    spreadSubhead: "",
    awayFill: 0,
    homeFill: 0,
    awayBoost: 0,
    homeBoost: 0,
    awayBoostLabel: "",
    homeBoostLabel: "",
    spreadResult: "" as const,
    spreadAxis: "",
  };
  if (entry.pick.betType !== "spread" || entry.pick.spread === null) return empty;
  if (typeof entry.game.homeScore !== "number" || typeof entry.game.awayScore !== "number") return empty;
  const pickedHome = entry.pick.side === "one";
  const pickedLine = pickedHome ? entry.pick.spread : -entry.pick.spread;
  const selectedScore = pickedHome ? entry.game.homeScore : entry.game.awayScore;
  const opposingScore = pickedHome ? entry.game.awayScore : entry.game.homeScore;
  const currentMargin = selectedScore - opposingScore;
  const coverMargin = currentMargin + pickedLine;
  const coverAt = Math.floor(-pickedLine) + 1;
  const finished = entry.game.isCompleted;
  let spreadHeadline = "AT THE NUMBER";
  let spreadSubhead = finished ? "The spread pushed" : "Need +1 to cover";
  if (coverMargin > 0) {
    spreadHeadline = finished ? "COVERED" : "COVERING ATS";
    spreadSubhead = `Covering by ${formatNumber(coverMargin)}`;
  } else if (coverMargin < 0) {
    const pointsToCover = Math.max(1, coverAt - currentMargin);
    spreadHeadline = finished ? "DIDN'T COVER" : "TRAILING ATS";
    spreadSubhead = finished
      ? `Missed by ${formatNumber(-coverMargin)}`
      : `Need +${pointsToCover} to cover`;
  }
  const bars = buildSpreadScoreBars({
    awayLabel: entry.game.awayAbbreviation,
    homeLabel: entry.game.homeAbbreviation,
    awayScore: entry.game.awayScore,
    homeScore: entry.game.homeScore,
    pickedSide: pickedHome ? "home" : "away",
    pickedLine,
    coverTone: coverMargin > 0 ? "positive" : coverMargin < 0 ? "negative" : "neutral",
  });
  const scale = bars.scale > 0 ? bars.scale : 1;
  return {
    spreadGraph: true,
    spreadHeadline,
    spreadSubhead,
    awayFill: bars.away.fill / scale,
    homeFill: bars.home.fill / scale,
    awayBoost: bars.away.spreadBoost / scale,
    homeBoost: bars.home.spreadBoost / scale,
    awayBoostLabel: bars.away.spreadBoost > 0 ? formatSigned(bars.away.spreadBoost) : "",
    homeBoostLabel: bars.home.spreadBoost > 0 ? formatSigned(bars.home.spreadBoost) : "",
    spreadResult: bars.result,
    spreadAxis: buildSpreadScoreAxisMarks(bars.away, bars.home, bars.scale).map((mark) => mark.label).join("   "),
  };
};

const spreadMark = (entry: ScoredPick, side: "home" | "away"): string => {
  if (entry.pick.betType !== "spread" || entry.pick.spread === null) return "";
  const pickedHome = entry.pick.side === "one";
  if ((side === "home") !== pickedHome) return "";
  return formatSigned(pickedHome ? entry.pick.spread : -entry.pick.spread);
};

const pickStatusSentence = (entry: ScoredPick): string => {
  if (entry.game.isCompleted) {
    if (entry.pick.betType === "moneyline" && entry.status === "TIED") return "TIED";
    if (entry.status === "PUSH" || (entry.margin === 0 && entry.pick.betType !== "moneyline")) return "PUSH";
    if (entry.tone === "good") return "PICK WON";
    if (entry.tone === "bad") return "PICK LOST";
    return "PUSH";
  }
  if (entry.pick.betType === "moneyline") {
    if (entry.status === "TIED") return "TIED";
    const diff = scoreDiff(entry.game);
    if (entry.tone === "good") return diff === null ? "AHEAD" : `AHEAD by ${diff}`;
    if (entry.tone === "bad") return diff === null ? "TRAILING" : `TRAILING by ${diff}`;
    return "TIED";
  }
  if (entry.margin === null) return entry.expandedStatus;
  if (entry.margin > 0) return `AHEAD by ${formatNumber(entry.margin)}`;
  if (entry.margin === 0) return "PUSH";
  return `TRAILING by ${formatNumber(Math.abs(entry.margin))}`;
};

const outcomeFor = (entry: ScoredPick): "live" | "won" | "lost" | "push" => {
  if (!entry.game.isCompleted) return "live";
  if (entry.status === "PUSH" || entry.margin === 0) return "push";
  if (entry.tone === "good") return "won";
  if (entry.tone === "bad") return "lost";
  return "push";
};

const scorePresentation = (focus: ScoredPick) => {
  const home = focus.game.homeAbbreviation;
  const away = focus.game.awayAbbreviation;
  const homeScore = focus.game.homeScore === null ? "–" : String(focus.game.homeScore);
  const awayScore = focus.game.awayScore === null ? "–" : String(focus.game.awayScore);
  const pickedHome = focus.pick.side === "one";
  const teamPick = focus.pick.betType !== "total";
  return {
    pickHeadline: pickKindLabel(focus),
    scorePrefix: teamPick ? "" : away,
    pickedScore: teamPick ? (pickedHome ? homeScore : awayScore) : awayScore,
    opponentScore: teamPick ? (pickedHome ? awayScore : homeScore) : homeScore,
    opponentAbbr: teamPick ? (pickedHome ? away : home) : home,
    pickedSide: teamPick ? (pickedHome ? "home" as const : "away" as const) : "" as const,
    awayMark: spreadMark(focus, "away"),
    homeMark: spreadMark(focus, "home"),
  };
};

const emptyPresentation = {
  pickHeadline: "",
  scorePrefix: "",
  pickedScore: "",
  opponentScore: "",
  opponentAbbr: "",
  pickedSide: "" as const,
  awayMark: "",
  homeMark: "",
  statusSentence: "",
  stakeLabel: "",
  aheadCount: 0,
  pickCount: 0,
  summaryTail: "",
  summaryTitle: "",
  headerDetail: "",
  potwChip: "",
  potwKind: "" as const,
  potwPlayerOne: "",
  potwPlayerTwo: "",
  potwValueOne: "",
  potwValueTwo: "",
  potwAbbrOne: "",
  potwAbbrTwo: "",
  potwPicked: "" as const,
  potwPickLabel: "",
  spreadGraph: false,
  spreadHeadline: "",
  spreadSubhead: "",
  awayFill: 0,
  homeFill: 0,
  awayBoost: 0,
  homeBoost: 0,
  awayBoostLabel: "",
  homeBoostLabel: "",
  spreadResult: "" as const,
  spreadAxis: "",
};

const emptyGameFields = {
  awayAbbr: "",
  homeAbbr: "",
  awayScore: "",
  homeScore: "",
  compactScore: "",
  pickLabel: "",
  compactStatus: "",
  expandedStatus: "",
  statusTone: "neutral" as const,
};

export function buildLiveActivitySnapshot(input: {
  now: Date;
  games: LiveActivityWeekGame[];
  picks: LiveActivityPick[];
  potw: LiveActivityPotw | null;
}): LiveActivitySnapshot | null {
  const gamesById = new Map(input.games.map((game) => [game.id, game]));
  const bestPickByGame = new Map<string, LiveActivityPick>();
  input.picks.forEach((pick) => {
    if (pick.betType === "custom_prop" || pick.betType === "total_tiebreaker") return;
    if (pick.side !== "one" && pick.side !== "two") return;
    const current = bestPickByGame.get(pick.gameId);
    if (!current || BET_PRIORITY[pick.betType] < BET_PRIORITY[current.betType]) {
      bestPickByGame.set(pick.gameId, pick);
    }
  });

  const scored = Array.from(bestPickByGame.values())
    .map((pick) => {
      const game = gamesById.get(pick.gameId);
      return game ? scorePick(game, pick, input.now) : null;
    })
    .filter((entry): entry is ScoredPick => entry !== null);

  const potwGame = input.potw ? gamesById.get(input.potw.gameId) : undefined;
  const potwKickoff = potwGame ? new Date(potwGame.gameTime).getTime() : Number.NaN;
  const potwMatchup = input.potw?.kind === "matchup";
  const potwLive = Boolean(
    input.potw
    && potwGame
    && Number.isFinite(potwKickoff)
    && potwKickoff <= input.now.getTime()
    && !potwGame.isCompleted
    && (potwMatchup || input.potw.line > 0),
  );

  const byKickoff = (a: ScoredPick, b: ScoredPick) =>
    new Date(a.game.gameTime).getTime() - new Date(b.game.gameTime).getTime();
  const live = scored.filter((entry) => entry.live).sort(byKickoff);
  const recentFinals = scored.filter((entry) => {
    if (!entry.game.isCompleted || !entry.started) return false;
    const kickoff = new Date(entry.game.gameTime).getTime();
    return input.now.getTime() - kickoff <= RECENT_FINAL_MS;
  });
  if (live.length === 0 && !potwLive && recentFinals.length === 0) return null;

  const finalWindowKey = recentFinals
    .slice()
    .sort(byKickoff)
    .at(-1)?.windowKey ?? null;
  const focusWindow = live[0]?.windowKey
    ?? (potwLive && potwGame ? windowParts(potwGame.gameTime)?.key : null)
    ?? finalWindowKey;
  const windowEntries = live.length > 0 || potwLive
    ? scored.filter((entry) => entry.started && entry.windowKey === focusWindow)
    : recentFinals.filter((entry) => entry.windowKey === focusWindow);
  const scoreCloseness = (entry: ScoredPick): number => {
    const diff = scoreDiff(entry.game);
    return diff === null ? Number.POSITIVE_INFINITY : diff;
  };
  const orderedRows = [...windowEntries].sort((a, b) => {
    if (a.live !== b.live) return a.live ? -1 : 1;
    const byScore = scoreCloseness(a) - scoreCloseness(b);
    if (byScore !== 0) return byScore;
    return byKickoff(a, b);
  });
  const displayRows = orderedRows.slice(0, LIVE_ACTIVITY_LOCK_SCREEN_ROWS);
  const ahead = windowEntries.filter((entry) => entry.tone === "good").length;
  const focusPool = live.length > 0 ? live : windowEntries;
  const focus = [...focusPool].sort((a, b) => {
    const aMargin = a.margin === null ? Number.POSITIVE_INFINITY : Math.abs(a.margin);
    const bMargin = b.margin === null ? Number.POSITIVE_INFINITY : Math.abs(b.margin);
    if (aMargin !== bMargin) return aMargin - bMargin;
    return new Date(a.game.gameTime).getTime() - new Date(b.game.gameTime).getTime();
  })[0] ?? null;
  const allFinal = windowEntries.length > 0 && windowEntries.every((entry) => entry.game.isCompleted);
  const slatePoints = windowEntries.reduce((sum, entry) => {
    const points = entry.pick.points ?? 0;
    if (allFinal) return sum + (entry.tone === "good" ? points : 0);
    return sum + (entry.live ? points : 0);
  }, 0);
  const presentation = focus
    ? {
        ...scorePresentation(focus),
        statusSentence: pickStatusSentence(focus),
        stakeLabel: windowEntries.length > 1
          ? (allFinal
            ? (slatePoints > 0 ? `+${formatPointsLabel(slatePoints)}` : "0 pts")
            : (slatePoints > 0 ? `Worth ${formatPointsLabel(slatePoints)}` : ""))
          : stakeLabelFor(focus.pick.points, outcomeFor(focus)),
        aheadCount: ahead,
        pickCount: windowEntries.length,
        summaryTail: windowEntries.length > 1
          ? `of ${windowEntries.length} ${allFinal ? "won" : "ahead"}`
          : "",
        summaryTitle: windowEntries.length > 1
          ? `${ahead} OF ${windowEntries.length} PICKS ${allFinal ? "WON" : "AHEAD"}`
          : "",
        headerDetail: windowEntries.length > 1
          ? (allFinal ? "FINAL" : `${windowEntries.length} GAMES LIVE`)
          : liveActivityClockLabel(focus.game),
        potwChip: "",
        potwKind: "" as const,
        potwPlayerOne: "",
        potwPlayerTwo: "",
        potwValueOne: "",
        potwValueTwo: "",
        potwAbbrOne: "",
        potwAbbrTwo: "",
        potwPicked: "" as const,
        potwPickLabel: "",
        ...spreadGraphFor(focus),
      }
    : emptyPresentation;

  const potw = input.potw;
  const potwRemaining = potw
    ? potw.line - potw.current
    : 0;
  const potwHit = potw
    ? (potw.side === "two" ? potw.current < potw.line : potw.current >= potw.line)
    : false;
  const potwOver = potw?.side === "two" && potw.current > potw.line;
  const matchupOne = potw?.current ?? 0;
  const matchupTwo = potw?.playerTwoValue ?? 0;
  const matchupDiff = matchupOne - matchupTwo;
  const matchupLeader: "" | "one" | "two" | "tie" = !potwMatchup
    ? ""
    : matchupDiff > 0
      ? "one"
      : matchupDiff < 0
        ? "two"
        : "tie";
  const pickedMatchup = potw?.side === "one" || potw?.side === "two" ? potw.side : "";
  const matchupChip = !potwMatchup
    ? ""
    : matchupLeader === "tie"
      ? "TIED"
      : !pickedMatchup
        ? matchupLeader === "one"
          ? `LEADING ${lastName(potw?.playerName ?? "")}`
          : `LEADING ${lastName(potw?.playerTwoName ?? "")}`
        : (pickedMatchup === "one" ? matchupDiff : -matchupDiff) > 0
          ? `AHEAD +${formatNumber(Math.abs(matchupDiff))}`
          : `TRAILING by ${formatNumber(Math.abs(matchupDiff))}`;
  const matchupPickLabel = pickedMatchup === "one"
    ? lastName(potw?.playerName ?? "")
    : pickedMatchup === "two"
      ? lastName(potw?.playerTwoName ?? "")
      : "";

  const gameFields = focus
    ? {
        awayAbbr: focus.game.awayAbbreviation,
        homeAbbr: focus.game.homeAbbreviation,
        awayScore: focus.game.awayScore === null ? "–" : String(focus.game.awayScore),
        homeScore: focus.game.homeScore === null ? "–" : String(focus.game.homeScore),
        compactScore: focus.game.homeScore === null || focus.game.awayScore === null
          ? focus.pickedAbbr
          : `${focus.pickedAbbr} ${focus.pick.side === "one" ? focus.game.homeScore : focus.game.awayScore}-${focus.pick.side === "one" ? focus.game.awayScore : focus.game.homeScore}`,
        pickLabel: focus.pickLabel,
        compactStatus: focus.status,
        expandedStatus: focus.expandedStatus,
        statusTone: focus.tone,
      }
    : emptyGameFields;

  return {
    windowLabel: focus?.windowLabel ?? (potwGame ? windowParts(potwGame.gameTime)?.label ?? "LIVE" : "LIVE"),
    aheadLabel: windowEntries.length > 0 ? `${ahead} OF ${windowEntries.length} PICKS AHEAD` : "PICK OF THE WEEK",
    focusKind: potwLive ? "potw" : "game",
    clockLabel: potwLive && potwGame ? liveActivityClockLabel(potwGame) : (focus ? liveActivityClockLabel(focus.game) : ""),
    ...gameFields,
    rows: displayRows.map((entry) => ({
      id: entry.game.id,
      awayAbbr: entry.game.awayAbbreviation,
      awayScore: entry.game.awayScore === null ? "–" : String(entry.game.awayScore),
      homeAbbr: entry.game.homeAbbreviation,
      homeScore: entry.game.homeScore === null ? "–" : String(entry.game.homeScore),
      status: entry.status,
      tone: entry.tone,
      clock: liveActivityClockLabel(entry.game),
      pickLabel: pickKindLabel(entry),
      chip: chipLabel(entry),
    })),
    potwActive: potwLive,
    potwCompactName: potw
      ? (potwMatchup ? lastName(matchupPickLabel || potw.playerName) : compactPlayerName(potw.playerName))
      : "",
    potwCurrent: potw
      ? (potwMatchup ? `${formatNumber(matchupOne)}–${formatNumber(matchupTwo)}` : formatNumber(potw.current))
      : "",
    potwLine: potw && !potwMatchup ? formatNumber(potw.line) : "",
    potwTitle: potw
      ? (potwMatchup ? potw.statLabel : `${lastName(potw.playerName)} ${potw.statLabel}`.trim())
      : "",
    potwRemaining: !potw || potwMatchup
      ? ""
      : potwOver
        ? "OVER"
        : potwHit
          ? "HIT"
          : `${formatNumber(potwRemaining)} TO GO`,
    potwProgress: potw && !potwMatchup && potw.line > 0 ? Math.min(1, Math.max(0, potw.current / potw.line)) : 0,
    ...presentation,
    potwChip: !potw
      ? ""
      : potwMatchup
        ? matchupChip
        : potwOver
          ? "TRAILING"
          : potwHit
            ? `AHEAD +${formatNumber(Math.abs(potw.current - potw.line))}`
            : `Needs ${formatNumber(Math.abs(potwRemaining))}`,
    potwKind: potw ? (potwMatchup ? "matchup" : "line") : "",
    potwPlayerOne: potwMatchup ? lastName(potw?.playerName ?? "") : "",
    potwPlayerTwo: potwMatchup ? lastName(potw?.playerTwoName ?? "") : "",
    potwValueOne: potwMatchup ? formatNumber(matchupOne) : "",
    potwValueTwo: potwMatchup ? formatNumber(matchupTwo) : "",
    potwAbbrOne: potwMatchup ? (potw?.playerOneAbbr ?? "") : "",
    potwAbbrTwo: potwMatchup ? (potw?.playerTwoAbbr ?? "") : "",
    potwPicked: potwMatchup ? pickedMatchup : "",
    potwPickLabel: potwMatchup && matchupPickLabel ? `YOUR PICK: ${matchupPickLabel}` : "",
    ...(potwLive && potwGame ? { headerDetail: liveActivityClockLabel(potwGame) } : {}),
    logoBase: "",
  };
}
